import { useEffect, useRef, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Container } from "@/components/site/Container";
import { HeroVideo } from "@/components/site/HeroVideo";
import { ManagedImage } from "@/components/site/ManagedImage";
import { PageHero } from "@/components/site/PageHero";
import heroVideoAsset from "@/assets/clogged-drain-hero.mp4.asset.json";
import heroPosterAsset from "@/assets/clogged-drain-hero-poster.jpg.asset.json";
import { ButtonLink } from "@/components/site/Button";
import { canonical } from "@/lib/seo";
import { ogImageMeta } from "@/lib/og-image";
import { submitLead } from "@/lib/leads.functions";
import { trackConversion } from "@/lib/openai-ads";
import { useLeadGuard } from "@/components/site/lead-honeypot";
import { captureOnLanding, getAttribution } from "@/lib/meta-attribution";
import { mirrorToGa4, trackStandard } from "@/lib/meta-events";
import { Phone, MessageSquare, CheckCircle2, ShieldCheck, Star, Clock, MapPin } from "lucide-react";

const TITLE = "Free Roof Inspection Phoenix — Photos + Straight Answers";
const DESCRIPTION =
  "Book a free roof inspection in Phoenix. We check foam, tile & shingle roofs, send photos of what we find, and tell you honestly if it needs work. Pick a time online.";


export const Route = createFileRoute("/free-roof-inspection-phoenix")({
  head: () => {
    const url = canonical("/free-roof-inspection-phoenix");
    const faqs = FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    }));
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        ...ogImageMeta("free-roof-inspection-phoenix"),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Roof Inspection",
            name: "Free Roof Inspection",
            areaServed: [
              "Phoenix, AZ",
              "Scottsdale, AZ",
              "Mesa, AZ",
              "Paradise Valley, AZ",
              "Cave Creek, AZ",
              "Glendale, AZ",
              "Chandler, AZ",
              "Tempe, AZ",
            ],
            provider: {
              "@type": "RoofingContractor",
              name: "Durafoam Roofing",
              telephone: "+1-480-941-5373",
              url: "https://www.durafoaminc.com",
            },
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://www.durafoaminc.com/" },
              { "@type": "ListItem", position: 2, name: "Free Roof Inspection", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: FreeInspectionPage,
});

const FAQS = [
  {
    q: "Is the inspection really free?",
    a: "Yes — 100% free with no obligation. We'll inspect your roof, take photos, and give you a written estimate at no cost.",
  },
  {
    q: "How long does an inspection take?",
    a: "Most inspections take 30 to 45 minutes depending on roof size and access.",
  },
  {
    q: "Do I need to be home?",
    a: "It helps so we can walk you through what we found, but it's not required. We can email the photo report and estimate.",
  },
  {
    q: "What if you find something?",
    a: "You'll get a written estimate with options — no high-pressure sales. You decide what (if anything) to do next.",
  },
  {
    q: "Do you inspect foam, tile, shingle, and flat roofs?",
    a: "Yes. We've been a Phoenix-specialty roofer since 1989 and inspect every common roof type in the Valley.",
  },
  {
    q: "How fast can you get out?",
    a: "Usually within 1–3 business days. For active leaks, call us and we'll prioritize same-day or next-day.",
  },
  {
    q: "What does a roof inspection include?",
    a: "A walk of the actual roof surface, plus every penetration and flashing (pipe boots, vents, HVAC and skylight curbs, wall and parapet flashings), drainage and ponding areas, coating thickness on foam, tile and underlayment condition, and interior or attic signs where you've noticed something. You get photos of each issue and a written assessment of remaining service life.",
  },
  {
    q: "How much does a roof inspection cost in Phoenix?",
    a: "Ours is free for homeowners and property managers. Paid inspections in the Valley generally run $150–$400 and are for documentation-heavy work — roof certifications for a home sale, insurance claim reports, or engineered assessments. If you need that kind of report, we'll tell you up front what it costs.",
  },
  {
    q: "Do I need a roof inspector near me, or will any roofer do?",
    a: "Use someone who works on your roof type in this climate. Foam, tile, and flat roofs each fail differently in Phoenix, and an inspector who mostly does shingles in a wet climate will misread all three. Ask whether they walk the roof, whether you get photos, and whether the estimate is written — those three answers separate a real inspection from a sales visit.",
  },
  {
    q: "Is a drone roof inspection good enough?",
    a: "Drones are a useful tool, not a substitute. Aerial photos are great for steep or unsafe roofs and for documenting storm damage across a whole roof at once, but a camera can't press on soft foam, lift a tile to check underlayment, check whether a pipe boot seal has hardened, or feel a spongy deck. We use aerial imagery where it helps and still put a person on the roof.",
  },
  {
    q: "How often should a roof be inspected in Arizona?",
    a: "Once a year for most Valley roofs, and again after any severe monsoon or hail event. Foam roofs should be checked yearly so the recoat happens on schedule instead of after a leak. Tile roofs past 15 years deserve a yearly look at the underlayment. Flat and commercial roofs do best on twice-a-year checks — before and after monsoon season.",
  },
  {
    q: "Do you inspect commercial buildings and rentals?",
    a: "Yes. We inspect commercial and multi-family roofs for property managers, HOAs, and building owners, including annual condition reports for budgeting. Access, units, and drainage all get documented.",
  },
  {
    q: "Will an inspection help with my insurance claim?",
    a: "It can. We photograph and describe storm-related damage in a way adjusters can use, and we can meet the adjuster on site. We won't tell you damage is storm-related when it's age — that helps nobody.",
  },
];

const REVIEWS = [
  {
    name: "Sarah M., Scottsdale",
    quote:
      "Durafoam recoated our foam roof last spring and it looks brand new. Crew was on time, cleaned up every day, and the price matched the estimate exactly.",
  },
  {
    name: "Mike R., Paradise Valley",
    quote:
      "Had a leak show up after a monsoon. They were out the next morning, found the spot, and had it patched by lunch. Honest and fast — that's rare.",
  },
  {
    name: "Jennifer T., Mesa",
    quote:
      "Got three inspections before choosing a roofer. Durafoam was the only one who told me my roof still had 5+ years left. Ended up using them for a small repair instead.",
  },
];

const CITIES = [
  "Phoenix", "Scottsdale", "Paradise Valley", "Mesa", "Chandler", "Tempe",
  "Cave Creek", "Carefree", "Fountain Hills", "Glendale", "Peoria", "Ahwatukee",
];

const STORAGE_KEY = "lead-form:free-roof-inspection-phoenix";

function readStoredForm() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      name: typeof parsed.name === "string" ? parsed.name : "",
      phone: typeof parsed.phone === "string" ? parsed.phone : "",
      email: typeof parsed.email === "string" ? parsed.email : "",
      message: typeof parsed.message === "string" ? parsed.message : "",
    };
  } catch {
    return null;
  }
}

function FreeInspectionPage() {
  const submit = useServerFn(submitLead);
  const navigate = useNavigate();
  const { honeypot, guardFields } = useLeadGuard();
  const formRef = useRef<HTMLFormElement>(null);
  const stored = readStoredForm();
  const [name, setName] = useState(stored?.name ?? "");
  const [phone, setPhone] = useState(stored?.phone ?? "");
  const [email, setEmail] = useState(stored?.email ?? "");
  const [message, setMessage] = useState(stored?.message ?? "");

  useEffect(() => {
    captureOnLanding();
    trackStandard("ViewContent", "vc:roof_health_check", {
      funnel: "roof_health_check",
      content_name: "Free Roof Health Check",
      content_category: "inspection",
    });
  }, []);

  function trackContact(kind: "call" | "text") {
    trackStandard("Contact", `contact:roof_health_check:${kind}`, {
      funnel: "roof_health_check",
      contact_method: kind,
    });
    mirrorToGa4("contact_click", { contact_method: kind, funnel: "roof_health_check" });
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    const id = window.setTimeout(() => {
      try {
        if (!name && !phone && !email && !message) {
          window.sessionStorage.removeItem(STORAGE_KEY);
        } else {
          window.sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ name, phone, email, message }),
          );
        }
      } catch {
        /* ignore quota / disabled storage */
      }
    }, 200);
    return () => window.clearTimeout(id);
  }, [name, phone, email, message]);

  const mutation = useMutation({
    mutationFn: (input: { name: string; phone: string; email: string; message: string }) =>
      submit({
        data: {
          name: input.name,
          phone: input.phone,
          email: input.email,
          service: "Free inspection",
          message: input.message,
          source: "free-roof-inspection-phoenix",
          attribution: getAttribution({
            funnel: "roof_health_check",
            lead_type: "free_inspection",
            form_name: "free_inspection_form",
          }),
          source_page: typeof window !== "undefined" ? window.location.pathname : "/free-roof-inspection-phoenix",
          ...guardFields(),
        },
      }),
    onSuccess: (res) => {
      if (res?.stored && res.lead_id) {
        trackConversion("form_submit", { dedupeKey: `form:${res.lead_id}`, leadId: res.lead_id });
      }
      if (res?.stored && res.event_id) {
        trackStandard(
          "Lead",
          `lead:${res.event_id}`,
          { funnel: "roof_health_check", lead_type: "free_inspection" },
          res.event_id,
        );
      }
      if (formRef.current) {
        formRef.current.reset();
        const fields = formRef.current.elements;
        for (const fieldName of ["name", "phone", "email", "message"]) {
          const field = fields.namedItem(fieldName);
          if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
            field.defaultValue = "";
            field.value = "";
          }
        }
      }
      setName(""); setPhone(""); setEmail(""); setMessage("");
      if (typeof window !== "undefined") {
        try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      }
      navigate({ to: "/thank-you" });
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Something went wrong. Please call us at 480-941-5373.");
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending || mutation.isSuccess) return;

    const fd = new FormData(e.currentTarget);
    const nameV = String(fd.get("name") ?? "").trim();
    const phoneV = String(fd.get("phone") ?? "").trim();
    const emailV = String(fd.get("email") ?? "").trim();
    const messageV = String(fd.get("message") ?? "").trim();

    if (nameV.length < 2) { toast.error("Please enter your name."); return; }
    if (phoneV.replace(/\D/g, "").length < 7) { toast.error("Please enter a valid phone number."); return; }

    mutation.mutate({ name: nameV, phone: phoneV, email: emailV, message: messageV });
  }

  return (
    <>
      {/* Hero */}
      <PageHero
        trustStrip={false}
        h1={<>Know What&rsquo;s Going On Up There!</>}
        subhead="Get a free, zero-obligation roof inspection and photo report across the Valley today. Catch hidden leaks before the storms hit."
        actions={
          <>
            <ButtonLink href="#book" variant="primary" size="lg" className="w-full sm:w-auto text-base">
              Book Free Inspection
            </ButtonLink>
            <ButtonLink
              href="tel:+14809415373"
              onClick={() => trackContact("call")}
              variant="secondary"
              size="lg"
              className="w-full sm:w-auto"
            >
              <Phone className="mr-2 h-5 w-5" aria-hidden="true" />
              <span className="sm:hidden">Call Us</span>
              <span className="hidden sm:inline">Call 480-941-5373</span>
            </ButtonLink>
          </>
        }
      >
        <div className="mt-4 lg:hidden">
          <a
            href="sms:+14809415373"
            onClick={() => trackContact("text")}
            className="inline-flex items-center gap-2 text-sm text-[rgba(255,255,255,0.85)] underline underline-offset-4 hover:text-white"
          >
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
            Or text us a question
          </a>
        </div>
      </PageHero>

      {/* Video */}
      <section className="bg-muted py-12 sm:py-16">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">See What We Find Up There</h2>
            <p className="mt-3 text-muted-foreground">
              A clogged drain can leave standing water on your roof, and that water leads to leaks. Here&rsquo;s what we look for and how we fix it.
            </p>
          </div>
          <div className="relative mx-auto mt-8 aspect-video w-full max-w-3xl overflow-hidden rounded-xl shadow-2xl">
            <HeroVideo
              src={heroVideoAsset.url}
              poster={heroPosterAsset.url}
              label="A clogged roof drain causing standing water on a foam roof, and how we fix it"
            />
          </div>
        </Container>
      </section>



      {/* Trust strip */}
      <section className="bg-background py-6">
        <Container>
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-primary">
            <li className="inline-flex items-center gap-2"><Star className="h-4 w-4 text-accent" aria-hidden="true" /> 4.8-Star Google Rated</li>
            <li className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" aria-hidden="true" /> Licensed &amp; insured (ROC)</li>
            <li className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-accent" aria-hidden="true" /> In Phoenix since 1989</li>
          </ul>
        </Container>
      </section>

      {/* Trust / proof */}
      <section className="bg-background py-12 lg:py-16">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-2xl font-bold text-primary sm:text-3xl">
              30,000+ roofs serviced across the Phoenix Valley since 1989
            </p>
            <p className="mt-2 text-foreground/70 text-sm">
              A few of the neighbors we&apos;ve helped recently.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {REVIEWS.map((r) => (
              <figure key={r.name} className="rounded-xl border border-border bg-white p-5 shadow-sm">
                <div className="flex gap-0.5 text-accent" aria-label="5 out of 5 stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="mt-3 text-[15px] text-foreground/85 leading-relaxed">
                  &ldquo;{r.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-3 text-sm font-semibold text-primary">{r.name}</figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-border bg-[var(--surface)]">
              <ManagedImage
                pageSlug="free-roof-inspection-phoenix"
                slot="proof-1"
                variant="card"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-border bg-[var(--surface)]">
              <ManagedImage
                pageSlug="free-roof-inspection-phoenix"
                slot="proof-2"
                variant="card"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* What you get */}
      <section className="bg-[var(--surface)] py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">What&apos;s included in your free inspection</h2>
            <p className="mt-3 text-foreground/80">
              A real, hands-on inspection by a Phoenix roofer — not a sales call.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "Full visual & photo report", d: "We get on the roof, take photos of every problem area, and walk you through what we found." },
              { t: "Leak, ponding & coating check", d: "We look for failed coating, blisters, soft spots, ponding water, flashing issues, and active leak paths." },
              { t: "Written estimate", d: "If repairs make sense, you'll get clear options and pricing — no high-pressure sales, ever." },
              { t: "Honest recommendations", d: "If your roof is fine, we'll tell you. We'd rather earn the next job than oversell this one." },
            ].map((b) => (
              <div key={b.t} className="rounded-xl border border-border bg-white p-5">
                <CheckCircle2 className="h-6 w-6 text-accent" aria-hidden="true" />
                <h3 className="mt-3 text-base font-semibold text-primary">{b.t}</h3>
                <p className="mt-2 text-sm text-foreground/80">{b.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Booking form */}
      <section id="book" className="bg-background py-14 lg:py-20 scroll-mt-24">
        <Container>
          <div className="mx-auto max-w-xl">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-primary sm:text-3xl">Get Your Free Inspection.</h2>
              <p className="mt-2 text-foreground/80">
                Fill out the form and we&apos;ll call you shortly. For immediate storm damage emergencies, tap &apos;Call Us&apos; below.
              </p>
            </div>

            <div className="mt-8 rounded-2xl border border-border bg-white p-6 sm:p-7 text-foreground shadow-sm">
              <form ref={formRef} onSubmit={onSubmit} className="relative space-y-4" noValidate>
                {honeypot}
                <Field id="f-name" label="Your name *">
                  <input id="f-name" name="name" type="text" autoComplete="name" defaultValue={name}
                    onChange={(e) => setName(e.target.value)} maxLength={80} required className={inputCls} />
                </Field>
                <Field id="f-phone" label="Phone *">
                  <input id="f-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" defaultValue={phone}
                    onChange={(e) => setPhone(e.target.value)} maxLength={20} required className={inputCls} />
                </Field>
                <Field id="f-email" label="Email (optional)">
                  <input id="f-email" name="email" type="email" autoComplete="email" defaultValue={email}
                    onChange={(e) => setEmail(e.target.value)} maxLength={120} className={inputCls} />
                </Field>
                <Field id="f-msg" label="What's going on? (optional)">
                  <textarea id="f-msg" name="message" rows={3} defaultValue={message}
                    onChange={(e) => setMessage(e.target.value)} maxLength={1000}
                    placeholder="Leaks, age of roof, recent storm damage, etc."
                    className={inputCls} />
                </Field>
                <button type="submit" disabled={mutation.isPending}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-6 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60">
                  {mutation.isPending ? "Sending…" : "Request my free inspection"}
                </button>
                <p className="text-xs text-[var(--muted-foreground)]">
                  By submitting, you agree we may call the number provided. We don&apos;t share your info.
                </p>
              </form>
            </div>
          </div>
        </Container>
      </section>

      {/* Service area */}
      <section className="bg-[var(--surface)] py-14 lg:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.4fr] lg:items-start">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">Service area</p>
              <h2 className="mt-2 text-2xl font-bold text-primary sm:text-3xl">Serving Phoenix and the entire Valley</h2>
              <p className="mt-3 text-foreground/80">
                We inspect roofs across Maricopa County, from downtown Phoenix to the far East
                Valley and North Scottsdale. If you&apos;re nearby and not listed, just ask.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm text-foreground/80">
                <MapPin className="h-4 w-4 text-accent" aria-hidden="true" />
                Phoenix, AZ • ROC licensed
              </div>
            </div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 text-foreground/85">
              {CITIES.map((c) => (
                <li key={c} className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-accent" aria-hidden="true" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* What the inspection covers */}
      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              What a free roof inspection actually covers
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-foreground/85">
              A senior estimator walks your roof — not the driveway — photographs everything worth
              seeing, and emails you a written assessment. It takes 30 to 60 minutes on a typical
              Phoenix home, and there is no cost and no obligation, including when the answer is
              &ldquo;you don&apos;t need work yet.&rdquo;
            </p>
            <ul className="mt-6 list-disc space-y-3 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li>
                <strong>Roof surface:</strong> coating thickness and wear on foam, cracked or slipped
                tile, blistering, ponding areas, and previous patchwork.
              </li>
              <li>
                <strong>Penetrations and flashings:</strong> pipe boots, vents, wall and parapet
                flashings, HVAC curbs, and skylight curbs — where most Phoenix leaks actually start.
              </li>
              <li>
                <strong>Drainage:</strong> scuppers, drains, and low spots that hold water after a
                monsoon storm.
              </li>
              <li>
                <strong>Interior signs:</strong> ceiling stains, soft drywall, and attic moisture
                where you&apos;ve noticed something inside.
              </li>
              <li>
                <strong>Remaining service life:</strong> a straight answer on how long what you have
                should last, and when the next recoat or repair is due.
              </li>
            </ul>

            <h3 className="mt-10 text-xl font-bold text-primary">What happens after the inspection</h3>
            <p className="mt-3 text-[16px] leading-relaxed text-foreground/85">
              You get photos of every issue plus a written scope and line-item price for anything we
              recommend — usually the same or next business day. Nothing is scheduled unless you say
              so. If the roof is fine, we tell you that and put a reminder on file for the next
              check.
            </p>

            <h3 className="mt-10 text-xl font-bold text-primary">The five things we find most often</h3>
            <ul className="mt-3 list-disc space-y-3 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li>
                <strong>An overdue foam coating.</strong> Phoenix sun burns through elastomeric in
                about 5 years. The fix is a{" "}
                <Link to="/foam-roof-coating-recoating" className={termLink}>recoat</Link>, not a new
                roof.
              </li>
              <li>
                <strong>Cracked pipe boots.</strong> The single most common leak source on Valley
                homes — a small{" "}
                <Link to="/roof-leak-repair" className={termLink}>leak repair</Link>, not a
                replacement.
              </li>
              <li>
                <strong>Tile broken by foot traffic.</strong> HVAC and satellite techs crack tile and
                expose the underlayment beneath —{" "}
                <Link to="/tile-roofing" className={termLink}>tile roofing work</Link>.
              </li>
              <li>
                <strong>Bird pecks and blisters in foam.</strong> Easy to patch early, expensive once
                water is under the surface —{" "}
                <Link to="/foam-roof-repair-phoenix" className={termLink}>foam roof repair</Link>.
              </li>
              <li>
                <strong>Underlayment at end of life under good tile.</strong> The tile gets reused;
                only the underlayment is replaced, which is far cheaper than a full{" "}
                <Link to="/roof-replacement-phoenix" className={termLink}>roof replacement</Link>.
              </li>
            </ul>
          </div>
        </Container>
      </section>

      {/* Search-intent section: roof inspection terms */}

      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              Roof inspection in Phoenix: which kind do you need?
            </h2>
            <ul className="mt-6 list-disc space-y-3 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li>
                <strong>Free roof inspection:</strong> what&apos;s on this page — a hands-on look with
                photos and a written estimate, at no cost and no obligation.
              </li>
              <li>
                <strong>Roof inspection cost:</strong> some inspections (certifications, detailed
                reports) are paid. See{" "}
                <Link to="/roof-inspection-cost-phoenix" className={termLink}>roof inspection costs in Phoenix</Link>.
              </li>
              <li>
                <strong>Roof certification for a home sale:</strong> buyers, sellers, and agents use{" "}
                <Link to="/roof-inspections-for-realtors" className={termLink}>roof inspections for realtors</Link>.
              </li>
              <li>
                <strong>Storm or monsoon damage inspection:</strong>{" "}
                <Link to="/storm-damage-repair" className={termLink}>storm damage roof repair</Link>.
              </li>
              <li>
                <strong>Foam roof inspection / recoat check:</strong> if your foam roof is 5+ years
                past its last coat, you likely need a{" "}
                <Link to="/foam-roof-coating-recoating" className={termLink}>recoat</Link>, not a
                replacement.
              </li>
              <li>
                <strong>Annual maintenance check:</strong>{" "}
                <Link to="/roof-maintenance" className={termLink}>roof maintenance programs</Link>.
              </li>
            </ul>
          </div>
        </Container>
      </section>



      {/* Inspection checklist by roof type */}
      <section className="bg-[var(--surface)] py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              The professional inspection checklist, by roof type
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-foreground/85">
              A real roof inspection isn&apos;t one checklist — it&apos;s four. Each Phoenix roof
              system fails in its own way, and knowing what to look for is the whole job. Here is
              exactly what we check.
            </p>

            <h3 className="mt-10 text-xl font-bold text-primary">Spray foam roofs</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li>Remaining coating thickness and chalking — the reflective topcoat is the sacrificial layer.</li>
              <li>Blisters, bird pecks, and pinholes; whether moisture has gotten below the foam.</li>
              <li>Soft or spongy areas underfoot, which indicate saturated foam that must be cut out.</li>
              <li>Terminations at parapets, curbs, and scuppers — where foam roofs usually open up first.</li>
              <li>Ponding and drainage slope; standing water shortens coating life dramatically.</li>
              <li>Date of last recoat versus the roughly 5-year Phoenix recoat cycle. See{" "}
                <Link to="/foam-roof-coating-recoating" className={termLink}>foam coating and recoating</Link>{" "}
                and <Link to="/foam-roof-repair-phoenix" className={termLink}>foam roof repair</Link>.
              </li>
            </ul>

            <h3 className="mt-10 text-xl font-bold text-primary">Tile roofs</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li>Cracked, slipped, and previously broken tile — usually from foot traffic.</li>
              <li>Underlayment age and condition, which is what actually waterproofs a tile roof.</li>
              <li>Valley metal for rust-through and debris packing.</li>
              <li>Headwall, sidewall, and chimney flashings; ridge and hip mortar.</li>
              <li>Missing bird stops and nesting at the eaves.</li>
              <li>Whether the roof needs{" "}
                <Link to="/tile-roof-repair-phoenix" className={termLink}>tile roof repair</Link>{" "}
                or a full underlayment replacement (<Link to="/tile-roofing" className={termLink}>tile roofing</Link>).
              </li>
            </ul>

            <h3 className="mt-10 text-xl font-bold text-primary">Shingle roofs</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li>Granule loss, curling, cupping, and heat blistering — Phoenix ages shingles fast.</li>
              <li>Missing or wind-lifted shingles and exposed nail heads.</li>
              <li>Sealed or failed shingle bond lines; brittleness at the edges.</li>
              <li>Step flashing, drip edge, valleys, and pipe boot seals.</li>
              <li>Attic ventilation, which drives how long the roof will last.
                See <Link to="/shingle-roofing" className={termLink}>shingle roofing</Link>.
              </li>
            </ul>

            <h3 className="mt-10 text-xl font-bold text-primary">Flat and low-slope roofs</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li>Seam and lap integrity on modified bitumen, TPO, or built-up systems.</li>
              <li>Ponding water, blocked scuppers and drains, and sagging deck areas.</li>
              <li>Punctures from HVAC service traffic and conduit supports.</li>
              <li>Base and counter-flashing at parapets; pitch pans around penetrations.</li>
              <li>Prior patchwork quality — layered patches usually hide the real problem.
                See <Link to="/flat-roof-repair" className={termLink}>flat roof repair</Link>.
              </li>
            </ul>
          </div>
        </Container>
      </section>

      {/* Residential vs commercial */}
      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              Residential vs. commercial roof inspections
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-foreground/85">
              The tools are the same; the scope and the paperwork are not.
            </p>
            <ul className="mt-6 list-disc space-y-3 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li>
                <strong>Residential roof inspection:</strong> 30–60 minutes on a typical Valley home.
                One roof, one report, photos of anything worth seeing, and a written estimate for any
                repair we recommend.
              </li>
              <li>
                <strong>Commercial roof inspection:</strong> larger areas, roof-mounted equipment, and
                far more penetrations, so it takes longer and includes a unit-by-unit look at HVAC
                curbs, drainage, and parapet details. Reports are written for budgeting and often
                include a repair-versus-replace recommendation with expected service life. See{" "}
                <Link to="/commercial-roofing" className={termLink}>commercial roofing</Link>.
              </li>
              <li>
                <strong>HOA and multi-family:</strong> multiple buildings inspected and reported
                together, with a prioritized list so the board can phase the work.{" "}
                <Link to="/hoa-roofing" className={termLink}>HOA roofing</Link>.
              </li>
              <li>
                <strong>Real estate and roof certifications:</strong> a documented condition report for
                a sale, on the buyer&apos;s or seller&apos;s timeline —{" "}
                <Link to="/roof-inspections-for-realtors" className={termLink}>roof inspections for realtors</Link>.
              </li>
            </ul>
          </div>
        </Container>
      </section>

      {/* How to choose an inspector + drone + frequency */}
      <section className="bg-[var(--surface)] py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              How to choose a roof inspector in Phoenix
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-foreground/85">
              &ldquo;Free roof inspection&rdquo; means very different things around the Valley. Some are
              genuine assessments; some are a sales visit with a clipboard. Five questions sort them
              out fast.
            </p>
            <ul className="mt-6 list-disc space-y-3 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li><strong>Do you walk the roof?</strong> A ground-level or binocular look is not an inspection.</li>
              <li><strong>Do I get photos?</strong> Photos of every issue, not stock images. If they can&apos;t show it, treat it as unverified.</li>
              <li><strong>Is the estimate written and itemized?</strong> A verbal number is a negotiating position, not a scope.</li>
              <li><strong>Are you licensed and insured in Arizona?</strong> Ask for the ROC number and verify it. Ours is ROC223900.</li>
              <li><strong>Will you tell me if I don&apos;t need work?</strong> The honest answer is sometimes &ldquo;your roof has five years left.&rdquo; If nobody ever says that, everything looks like a replacement.</li>
            </ul>

            <h3 className="mt-10 text-xl font-bold text-primary">Where drone inspections help — and where they don&apos;t</h3>
            <p className="mt-3 text-[16px] leading-relaxed text-foreground/85">
              Aerial imagery is genuinely useful: it documents a whole roof quickly, reaches steep or
              unsafe slopes, and gives adjusters a clean overview after a storm. What a drone cannot
              do is press on soft foam, lift a tile to check the underlayment, test whether a pipe
              boot seal has hardened, or feel a deck that has gone spongy — and those are the findings
              that change a decision. Treat drone-only inspections as a screening, not a diagnosis.
            </p>

            <h3 className="mt-10 text-xl font-bold text-primary">How often to inspect a roof in Phoenix</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-[16px] leading-relaxed text-foreground/85">
              <li><strong>Every year</strong> for any roof — ideally in spring, before monsoon season.</li>
              <li><strong>After every severe storm</strong>, especially wind and hail events —{" "}
                <Link to="/storm-damage-repair" className={termLink}>storm damage repair</Link> and{" "}
                <Link to="/monsoon-roof-inspection" className={termLink}>monsoon roof inspections</Link>.
              </li>
              <li><strong>Foam roofs:</strong> yearly, so the recoat happens on schedule rather than after a leak.</li>
              <li><strong>Tile roofs past 15 years:</strong> yearly, with attention to underlayment condition.</li>
              <li><strong>Flat and commercial roofs:</strong> twice a year, before and after monsoon.</li>
              <li><strong>Before buying or selling:</strong> always, and before a warranty expires.</li>
            </ul>
            <p className="mt-6 text-[16px] leading-relaxed text-foreground/85">
              Not sure whether what you&apos;re seeing is urgent? Our guide to{" "}
              <Link to="/blog/$slug" params={{ slug: "signs-of-roof-damage" }} className={termLink}>signs of roof damage</Link>{" "}
              walks through what to look for from the ground, and{" "}
              <Link to="/roof-maintenance" className={termLink}>roof maintenance</Link> covers keeping
              a healthy roof healthy.
            </p>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">Free inspection FAQ</h2>
            <dl className="mt-8 divide-y divide-border rounded-2xl border border-border bg-white">
              {FAQS.map((f) => (
                <div key={f.q} className="p-5 sm:p-6">
                  <dt className="text-base font-semibold text-primary">{f.q}</dt>
                  <dd className="mt-2 text-foreground/85">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="bg-primary py-12 lg:py-16 text-white">
        <Container>
          <div className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
            <div>
              <h2 className="text-2xl font-bold sm:text-3xl">Ready for your free roof inspection?</h2>
              <p className="mt-2 text-[rgba(255,255,255,0.9)]">
                Call now or book online — we respond right away.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <ButtonLink href="tel:+14809415373" variant="primary" size="lg" onClick={() => trackContact("call")}>
                <Phone className="mr-2 h-5 w-5" aria-hidden="true" />
                <span className="sm:hidden">Call Us</span>
                <span className="hidden sm:inline">480-941-5373</span>
              </ButtonLink>
              <ButtonLink href="sms:+14809415373" variant="secondary" size="lg" className="sm:hidden" onClick={() => trackContact("text")}>
                <MessageSquare className="mr-2 h-5 w-5" aria-hidden="true" />
                Text Us
              </ButtonLink>
              <ButtonLink href="#book" variant="secondary" size="lg">
                Book Free Inspection
              </ButtonLink>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

const termLink = "font-semibold text-primary underline underline-offset-2 hover:text-accent";

const inputCls =
  "w-full rounded-lg border border-border bg-white px-4 py-3 text-[15px] text-foreground placeholder:text-[var(--muted-foreground)] focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30";

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
